'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { IconBell } from '@tabler/icons-react';
import type { Notification as NotificationType } from '@prisma/client';
import { useSession } from 'next-auth/react';
import { formatDistanceToNow } from 'date-fns';
import { useNotificationStore } from '@/stores/useNotificationStore';
import classes from './NotificationDropdown.module.css';


function getNotificationUrl(notification: NotificationType, currentUserId: string): string {
  const { type, meta } = notification;

  // Handle special cases that don't need mentor/mentee routing
  switch (type) {
    case 'mentor_application_processed':
      return meta && (meta as any).status === 'ACCEPTED' ? '/account/profile?mentor_profile=true' : '/mentoring/apply';
  }

  // Handle mentor/mentee routing for other notification types
  const { mentorUserId, menteeUserId } = meta as any;

  if (!mentorUserId || !menteeUserId) {
    return '#';
  }

  const isRecipientMentor = currentUserId === mentorUserId;
  const basePath = isRecipientMentor ? '/mentoring/messages' : '/learning/messages';
  const otherUserId = isRecipientMentor ? menteeUserId : mentorUserId;

  switch (type) {
    case 'new_message':
    case 'mentorship_application_status_updated_mentor':
    case 'mentorship_application_status_updated_mentee':
    case 'task_reminder':
    case 'meeting_transcription_ready':
      return `${basePath}/${otherUserId}`;
    case 'meeting_created':
      return '/calendar';
    default:
      return '#';
  }
}

interface NotificationDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function NotificationDropdown({ isOpen, onToggle }: NotificationDropdownProps) {
  const { data: session } = useSession();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, fetchNotifications, markAllAsRead, markOneAsRead } = useNotificationStore();

  useEffect(() => {
    if (session?.user?.id) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 15000); // Poll every 15 seconds
      return () => clearInterval(interval);
    }
  }, [session, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onToggle();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const handleOpen = async () => {
    onToggle();

    if (!isOpen && unreadCount > 0) {
      markAllAsRead();
    }
  };

  const handleNotificationClick = (notificationId: string) => {
    markOneAsRead(notificationId);
    onToggle();
  };

  return (
    <div className={classes.wrapper} ref={dropdownRef}>
      <button onClick={handleOpen} className={`${classes.notificationButton} ${isOpen ? classes.notificationButtonOpen : ''}`}>
        <IconBell size={24} />
        {unreadCount > 0 && (
          <div className={classes.badge}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
      </button>
      {isOpen && (
        <div className={classes.dropdown}>
          <ul className={classes.list}>
            {notifications?.length > 0 ? (
              notifications.map((notification) => (
                <Link 
                  key={notification.id} 
                  href={getNotificationUrl(notification, session!.user!.id!)} 
                  className={classes.notificationLink}
                  onClick={() => handleNotificationClick(notification.id)}
                >
                  <li className={`${classes.notificationItem} ${notification.readAt ? classes.read : ''}`}>
                    <div className={classes.content}>
                      <p
                        className={classes.message}
                        dangerouslySetInnerHTML={{ __html: (notification as any).message || 'You have a new notification.' }}
                      />
                      <p className={classes.timestamp}>
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </li>
                </Link>
              ))
            ) : (
              <li className={classes.emptyState}>You have no notifications.</li>
            )}
          </ul>
          <div className={classes.footer}>
            <Link href="/notifications" className={classes.seeAllButton} onClick={onToggle}>
              See all
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
